import React, { useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { Loader2, Sparkles, PenTool, FileText, MessageSquare, Wand2 } from "lucide-react";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { requiredField } from "../lib/validation";

// modules/ai_agent.py's AdminAssistantIn.prompt has no length ceiling of its own; 8000 is
// a generous cap against an accidental paste rather than a mirrored backend bound.
// `requiredField` emits common-namespace keys, resolved below through `tc`, not `t` — the
// two messages this field can show (required / too long) must resolve from the SAME
// dictionary, or one of them silently falls back to the JSX fallback text instead.
const PROMPT_SCHEMA = z.object({ prompt: requiredField(8000) });

const TASKS = [
  { id: "write_article", icon: PenTool },
  { id: "draft_reply", icon: MessageSquare },
  { id: "summarize_messages", icon: FileText },
  { id: "free_form", icon: Wand2 },
];

export default function AiAssistantAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const [task, setTask] = useState("write_article");
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const submit = async () => {
    if (validate(PROMPT_SCHEMA, { prompt })) { toast.error(tc("validation.fixHighlighted")); return; }
    setLoading(true);
    setResponse("");
    try {
      const { data } = await api.post("/ai/admin/assistant", { task, prompt });
      setResponse(data.response);
    } catch (e) {
      // Plan and build refusals (CAPABILITY_NOT_IN_PLAN / _NOT_BUILT) are named by
      // extractApiError through the common namespace; the route guard keeps this page
      // unreachable in the first place.
      toast.error(extractApiError(e, t("ai.error"), tc));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(response);
    toast.success(t("ai.copied"));
  };

  const currentTask = TASKS.find((tk) => tk.id === task);

  return (
    <div data-testid="ai-assistant" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-amber-500" /> {t("ai.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">
          {t("ai.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {TASKS.map((tk) => {
          const Icon = tk.icon;
          return (
            <button
              key={tk.id}
              onClick={() => setTask(tk.id)}
              data-testid={`task-${tk.id}`}
              className={`p-4 rounded-2xl border text-left transition ${
                task === tk.id
                  ? "border-eh-ink bg-eh-ink text-eh-bg"
                  : "border-eh-border-soft bg-white hover:border-eh-ink/30"
              }`}
            >
              <Icon className="w-5 h-5 mb-2" />
              <div className="text-sm font-medium">{t(`ai.task.${tk.id}`)}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
        <Label className="text-sm mb-2 block">{t("ai.yourInput")}</Label>
        <textarea
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); clearFieldError("prompt"); }}
          data-testid="ai-prompt"
          rows={6}
          placeholder={t(`ai.ph.${currentTask?.id}`)}
          className={`w-full border rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-eh-sage-deep/30 ${fieldErrors.prompt ? invalidInputClass : ""}`}
        />
        <FieldError message={fieldErrors.prompt ? tc(fieldErrors.prompt) : ""} testId="ai-prompt-error" />
        <Button
          onClick={submit}
          disabled={loading}
          data-testid="ai-submit"
          className="mt-4 bg-eh-ink text-eh-bg"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {t("ai.generate")}
        </Button>
      </div>

      {response && (
        <div className="bg-amber-50/40 border border-amber-200 rounded-2xl p-6" data-testid="ai-response">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-eh-ink">{t("ai.response")}</div>
            <Button size="sm" variant="outline" onClick={copy} data-testid="ai-copy">{t("ai.copy")}</Button>
          </div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-eh-ink">{response}</div>
        </div>
      )}
    </div>
  );
}
